import type { ReactNode } from "react";

import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { ArrowRight, ButtonLink } from "./Button";
import { ExternalLink } from "./ExternalLink";
import { MediaImage } from "./MediaImage";
import { Reveal } from "./Reveal";
import { useMediaByKey } from "@/content-context";
import type { LinkDoc, PageDoc } from "@/content";
import { BANNER_SCRIM, BANNER_TEXT_ALPHA } from "@/lib/contrast";
import { fadeUp, staggerContainer } from "@/lib/motion";
import { usePageData } from "@/page-data";

/**
 * Interior-page masthead: breadcrumb, small label, one H1, one supporting line,
 * and optionally a description and the page's primary actions.
 *
 * Everything on it comes from the page record where the page has one, and from
 * the props the route passes where it does not. That is the page's banner in the
 * editor: label, heading, supporting line, description, up to two buttons,
 * whether the breadcrumb shows, and a photograph.
 *
 * Without a photograph it sits on ivory, as it always has, so the white floating
 * header reads clearly above it. With one, the photograph fills the masthead
 * behind a scrim whose strength is fixed by calculation rather than by eye; see
 * BANNER_SCRIM. The photograph variant renders without reveal animations, because
 * it is the first thing a phone paints and must not wait for hydration.
 */
export function PageHeader({
  eyebrow,
  title,
  lead,
  trail,
  tone = "ivory",
  actions,
  page: pageOverride,
}: {
  eyebrow?: string;
  title: string;
  lead?: string;
  trail: Crumb[];
  tone?: "mist" | "ivory" | "white";
  /** The page's primary call to action, used when the page record has no banner buttons. */
  actions?: ReactNode;
  /** A page record from somewhere other than the route's page data, such as a page built in the panel. */
  page?: PageDoc | null;
}) {
  const data = usePageData();
  const byKey = useMediaByKey();
  const page = pageOverride ?? data?.page ?? null;
  const banner = page?.banner;

  const visibleEyebrow = page?.eyebrow || eyebrow;
  const visibleTitle = page?.headline || title;
  const visibleLead = page?.lead || lead;
  const description = banner?.description;
  const showBreadcrumb = banner?.showBreadcrumb !== false;
  const image = byKey(banner?.mediaKey);
  const buttons = sortedLinks(banner?.ctas);

  if (image) {
    return (
      <header className="relative isolate flex min-h-[26rem] items-end overflow-hidden bg-midnight pb-14 pt-28 md:min-h-[32rem] md:pb-20 md:pt-36">
        <MediaImage
          item={image}
          fill
          priority
          sizes="100vw"
          className="object-cover"
          objectPosition={banner?.focalPoint || image.objectPosition || "50% 50%"}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 lg:hidden"
          style={{ backgroundColor: `rgba(12,29,51,${BANNER_SCRIM.copy})` }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 hidden lg:block"
          style={{
            background: `linear-gradient(90deg, rgba(12,29,51,${BANNER_SCRIM.copy}) 0%, rgba(12,29,51,${BANNER_SCRIM.copy}) ${BANNER_SCRIM.copyWidthPercent + 4}%, rgba(12,29,51,${BANNER_SCRIM.edge}) 82%, rgba(12,29,51,${BANNER_SCRIM.far}) 100%)`,
          }}
        />

        <div className="container-page relative z-10 w-full">
          {/* Held inside the evenly dark part of the scrim: BANNER_SCRIM.copyWidthPercent. */}
          <div className="max-w-4xl lg:max-w-[56%]">
            {showBreadcrumb ? <Breadcrumbs trail={trail} tone="light" /> : null}
            {visibleEyebrow ? (
              <p className="eyebrow mt-8 text-white" style={{ opacity: BANNER_TEXT_ALPHA.eyebrow }}>
                {visibleEyebrow}
              </p>
            ) : null}
            <h1 className="mt-4 text-balance text-white" style={{ fontSize: "var(--step-h1)" }}>
              {visibleTitle}
            </h1>
            {visibleLead ? (
              <p className="mt-5 max-w-2xl text-white" style={{ fontSize: "var(--step-lead)", opacity: BANNER_TEXT_ALPHA.lead }}>
                {visibleLead}
              </p>
            ) : null}
            {description ? (
              <p className="mt-4 max-w-2xl text-white" style={{ opacity: BANNER_TEXT_ALPHA.description }}>
                {description}
              </p>
            ) : null}
            {buttons.length ? (
              <div className="mt-8">
                <BannerButtons links={buttons} light />
              </div>
            ) : actions ? (
              <div className="mt-8">{actions}</div>
            ) : null}
          </div>
        </div>
      </header>
    );
  }

  const bg = tone === "mist" ? "bg-mist" : tone === "white" ? "bg-white" : "bg-ivory";

  return (
    <header className={`${bg} pb-12 pt-28 md:pb-16 md:pt-36`}>
      <div className="container-page">
        {showBreadcrumb ? <Breadcrumbs trail={trail} /> : null}

        <Reveal variants={staggerContainer} className={`${showBreadcrumb ? "mt-8" : ""} max-w-4xl`}>
          {visibleEyebrow ? (
            <Reveal variants={fadeUp}>
              <p className="eyebrow">{visibleEyebrow}</p>
            </Reveal>
          ) : null}

          <Reveal variants={fadeUp}>
            <h1 className="mt-4" style={{ fontSize: "var(--step-h1)" }}>
              {visibleTitle}
            </h1>
          </Reveal>

          {visibleLead ? (
            <Reveal variants={fadeUp}>
              <p className="mt-5 max-w-2xl text-muted" style={{ fontSize: "var(--step-lead)" }}>
                {visibleLead}
              </p>
            </Reveal>
          ) : null}

          {description ? (
            <Reveal variants={fadeUp}>
              <p className="mt-4 max-w-2xl text-muted">{description}</p>
            </Reveal>
          ) : null}

          {buttons.length || actions ? (
            <Reveal variants={fadeUp}>
              <div className="mt-8">{buttons.length ? <BannerButtons links={buttons} /> : actions}</div>
            </Reveal>
          ) : null}
        </Reveal>
      </div>
    </header>
  );
}

function sortedLinks(links?: LinkDoc[]) {
  return (links ?? [])
    .filter((link) => !link.hidden && link.label && link.href)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Up to two buttons: the first is the primary action, the second sits beside it. */
function BannerButtons({ links, light = false }: { links: LinkDoc[]; light?: boolean }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      {links.slice(0, 2).map((link, index) =>
        link.external || /^https?:\/\//i.test(link.href) ? (
          <ExternalLink key={link.href + link.label} href={link.href} variant={light ? "buttonLight" : "button"}>
            {link.label}
          </ExternalLink>
        ) : (
          <ButtonLink
            key={link.href + link.label}
            href={link.href}
            variant={index === 0 ? (light ? "light" : "primary") : light ? "ghostLight" : "secondary"}
          >
            {link.label}
            {index === 0 ? <ArrowRight /> : null}
          </ButtonLink>
        ),
      )}
    </div>
  );
}
